/**
 * Types
 */
type ValidateDate = (date: string | number | Date) => boolean
type GetDate = <T>(value: T) => string | number | Date

/**
 * 日付を数値に変換
 * @param date 日付
 * @returns 日付を数値に変換
 */
export const toDateTime = (date: string | number | Date): number => {
  switch (typeof date) {
    case 'string':
      return new Date(date).getTime()
    case 'number':
      return date
    default:
      return date.getTime()
  }
}

/**
 * 基準となる日付よりも未来かどうか
 * @param base 基準となる日付
 * @param date 日付
 * @returns 基準となる日付よりも未来かどうか
 */
export const isFuture
  = (base: string | number | Date) =>
    (date: string | number | Date): boolean => {
      return toDateTime(date) > toDateTime(base)
    }

/**
 * 基準となる日付よりも過去かどうか
 * @param base 基準となる日付
 * @param date 日付
 * @returns 基準となる日付よりも過去かどうか
 */
export const isPast
  = (base: string | number | Date) =>
    (date: string | number | Date): boolean => {
      return toDateTime(date) < toDateTime(base)
    }

/**
 * 基準となる日付以降かどうか
 * @param base 基準となる日付
 * @param date 日付
 * @returns 基準となる日付以降かどうか
 */
export const isFutureOrSame
  = (base: string | number | Date) =>
    (date: string | number | Date): boolean => {
      return toDateTime(date) >= toDateTime(base)
    }

/**
 * 基準となる日付以前かどうか
 * @param base 基準となる日付
 * @param date 日付
 * @returns 基準となる日付以前かどうか
 */
export const isPastOrSame
  = (base: string | number | Date) =>
    (date: string | number | Date): boolean => {
      return toDateTime(date) <= toDateTime(base)
    }

/**
 * 日付が含まれる任意の値の配列のうち、任意のバリデーションを満たすものを返す
 * @param validateDate 日付をバリデーションする関数
 * @param array 日付が含まれる任意の値の配列
 * @param getDate 日付を取得する関数
 * @returns 日付が含まれる任意の値の配列のうち、任意のバリデーションを満たすもの
 */
const filterDate
  = <T = any>(validateDate: ValidateDate) =>
    (array: T[], getDate: GetDate = (value: any) => value): T[] => {
      return array.filter((value) => {
        return validateDate(getDate(value))
      })
    }

/**
 * 日付が含まれる任意の値の配列のうち、日付が基準となる日付よりも未来のものを返す
 * @param base 基準となる日付
 * @param array 日付が含まれる任意の値の配列
 * @param getDate 日付を取得する関数
 * @returns 日付が基準となる日付よりも未来のもの
 */
export const filterFuture
  = <T>(base: string | number | Date) =>
    (array: T[], getDate?: GetDate): T[] => {
      return filterDate(isFuture(base))(array, getDate)
    }

/**
 * 日付が含まれる任意の値の配列のうち、日付が基準となる日付よりも過去のものを返す
 * @param base 基準となる日付
 * @param array 日付が含まれる任意の値の配列
 * @param getDate 日付を取得する関数
 * @returns 日付が基準となる日付よりも過去のもの
 */
export const filterPast
  = <T>(base: string | number | Date) =>
    (array: T[], getDate?: GetDate): T[] => {
      return filterDate(isPast(base))(array, getDate)
    }
